import styled from "@emotion/styled";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { Button, Typography, useBreakpoint } from "@avenue-8/sidekick-ui";
import {
  PresentationsList,
  NewPresentationDialog,
  PresentationsLayout,
  formatPresentationTitle,
} from "@features/Presentation";
import { useListCmas } from "@cma/shared/queries/cma/useListCmas";
import { useDeleteCma } from "@cma/shared/queries/cma/useDeleteCma";
import { useChatLogic } from "@features/Chat/useChatLogic";
import { withMainLayout } from "@shared/layouts/withMainLayout";
import { useInfiniteScroll } from "@shared/hooks/useInfiniteScroll";
import { useChatWidgetLogic } from "@shared/hooks/useChatWidgetLogic";
import { useUIStore } from "@store/ui.store";
import { usePageNavbar } from "@shared/components/usePageNavbar";
import { PageMoreActions } from "@shared/components/PageMoreActions";

const PageContent = styled.div`
  padding-top: 1rem;
`;

function PresentationsListPageContent() {
  const navigate = useNavigate();
  const [isNewPresentationDialogOpen, setIsNewPresentationDialogOpen] =
    useState(false);

  const {
    presentations,
    totalItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useListCmas();
  const deleteMutation = useDeleteCma();
  const { handleSendMessage, isSendingMessage } = useChatLogic({
    currentThread: undefined,
    navigateToThread: (id) => navigate(id ? `/chat/${id}` : "/"),
    canAdoptTemporaryWidget: false,
  });
  const { setChatWidget, toggleChatWidgetDrawer } = useChatWidgetLogic();
  const isLaptopDown = useBreakpoint("laptop");

  const confirmDialog = useUIStore((state) => state.confirmDialog);

  const { sentinelRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const handleView = (id: string) => {
    window.open(`${window.location.origin}/presentations/${id}/view`, "_blank");
  };

  const handleEdit = (id: string) => {
    setChatWidget({
      kind: "widget-cma-details",
      artifactId: null,
      content: { cmaId: id },
      temporary: true,
    });
    toggleChatWidgetDrawer(isLaptopDown ? "collapsed" : "expanded");
    navigate("/");
  };

  const handleCopyLink = (id: string) => {
    const url = `${window.location.origin}/presentations/${id}/view`;
    navigator.clipboard.writeText(url);
  };

  const handleDelete = async (id: string) => {
    const presentation = presentations.find((p) => p.id === id);
    const title = presentation ? formatPresentationTitle(presentation) : "";

    const confirmed = await confirmDialog({
      title: "Delete presentation?",
      content: (
        <Typography>
          This will delete <strong>{title}</strong>
        </Typography>
      ),
      confirmButtonText: "Delete",
    });

    if (!confirmed) return;

    deleteMutation.mutate(id);
  };

  const handleCreateNewSubmit = (message: string) => {
    handleSendMessage({ message });
  };

  const handleOpenNewPresentationDialog = useCallback(
    () => setIsNewPresentationDialogOpen(true),
    [],
  );

  const header = usePageNavbar({
    title: "Presentations",
    rightElement: (
      <Box display="flex" alignItems="center" gap={2} pr={2}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenNewPresentationDialog}
        >
          Create New
        </Button>
        <PageMoreActions />
      </Box>
    ),
  });

  return (
    <PresentationsLayout header={header}>
      <PageContent>
        <PresentationsList
          presentations={presentations}
          isLoading={isLoading}
          totalItems={totalItems}
          isFetchingMore={isFetchingNextPage}
          hasMore={hasNextPage}
          sentinelRef={sentinelRef}
          onView={handleView}
          onEdit={handleEdit}
          onCopyLink={handleCopyLink}
          onDelete={handleDelete}
        />
        <NewPresentationDialog
          open={isNewPresentationDialogOpen}
          isSubmitting={isSendingMessage}
          onClose={() => setIsNewPresentationDialogOpen(false)}
          onSubmit={handleCreateNewSubmit}
        />
      </PageContent>
    </PresentationsLayout>
  );
}

export const PresentationsListPage = withMainLayout(
  PresentationsListPageContent,
);
